import { useState, useEffect } from "react";
import { dbFetchSubscriberByToken, dbFetchProviderBrand, dbSavePortalPause } from "./supabase";
import PauseModal, { findPauseConflict } from "./PauseModal";

// ── Shared calculation helpers (mirrors SubscriberManagement) ─────────────────
const DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const calcTotalMeals = (totalDays, mealSlots) =>
  (Number(totalDays) || 0) * ((mealSlots || []).length || 1);

const isPausedOnDate = (dateStr, pausePeriods, slot) => {
  if (!pausePeriods || pausePeriods.length === 0) return false;
  return pausePeriods.some(p => {
    if (dateStr < p.from || dateStr > p.to) return false;
    return (p.slots || []).includes(slot);
  });
};

const calcMealsDelivered = (startDateStr, endDateStr, excludedDays, pausePeriods, mealSlots, totalMeals) => {
  if (!startDateStr) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(startDateStr);
  if (today < start) return 0;
  const end   = endDateStr ? new Date(endDateStr) : today;
  const limit = today < end ? today : end;
  const excSet = new Set((excludedDays || []).map(d => DAY_INDEX[d]));
  const slots  = mealSlots && mealSlots.length > 0 ? mealSlots : ["Lunch"];
  let count = 0;
  const d = new Date(start);
  while (d <= limit) {
    if (!excSet.has(d.getDay())) {
      const ds = d.toISOString().slice(0, 10);
      for (const slot of slots) {
        if (!isPausedOnDate(ds, pausePeriods, slot)) count++;
      }
    }
    d.setDate(d.getDate() + 1);
  }
  return totalMeals ? Math.min(count, totalMeals) : count;
};

const calcEndDate = (startDateStr, totalDays, excludedDays) => {
  if (!startDateStr || !totalDays) return "";
  const excSet = new Set((excludedDays || []).map(d => DAY_INDEX[d]));
  const d = new Date(startDateStr);
  let counted = 0;
  while (counted < totalDays) {
    if (!excSet.has(d.getDay())) counted++;
    if (counted < totalDays) d.setDate(d.getDate() + 1);
  }
  return d.toISOString().slice(0, 10);
};

const calcEndDateWithPauses = (sub, periods) => {
  if (!sub.startDate || !sub.totalDays) return sub.endDate;
  const excSet      = new Set((sub.excludedDays || []).map(d => DAY_INDEX[d]));
  const mealSlots   = sub.mealSlots && sub.mealSlots.length > 0 ? sub.mealSlots : ["Lunch"];
  const slotsPerDay = mealSlots.length;

  // Count paused MEAL-SLOTS (not raw days) so single-slot pauses extend correctly
  let pausedMeals = 0;
  periods.forEach(p => {
    const d = new Date(p.from), to = new Date(p.to);
    while (d <= to) {
      if (!excSet.has(d.getDay())) {
        mealSlots.forEach(slot => {
          if ((p.slots || []).includes(slot)) pausedMeals++;
        });
      }
      d.setDate(d.getDate() + 1);
    }
  });

  const extraDays = Math.ceil(pausedMeals / slotsPerDay);
  const baseEnd = calcEndDate(sub.startDate, sub.totalDays, sub.excludedDays);
  if (extraDays === 0) return baseEnd;
  const d = new Date(baseEnd);
  let added = 0;
  while (added < extraDays) {
    d.setDate(d.getDate() + 1);
    if (!excSet.has(d.getDay())) added++;
  }
  return d.toISOString().slice(0, 10);
};

// ── Formatting helpers ────────────────────────────────────────────────────────
const fmt     = (n) => "₹" + Number(n).toLocaleString("en-IN");
const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const SLOT_COLORS  = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" };
const TYPE_COLORS  = { Veg: "#1D9E75", "Non-Veg": "#D85A30", Vegan: "#534AB7", Jain: "#BA7517" };
const STATUS_STYLE = {
  Active:   { bg: "#EAF3DE", color: "#27500A" },
  Pending:  { bg: "#FAEEDA", color: "#633806" },
  Overdue:  { bg: "#FCEBEB", color: "#791F1F" },
  Paused:   { bg: "#E6F1FB", color: "#0C447C" },
  Inactive: { bg: "#F1EFE8", color: "#5F5E5A" },
};

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, ok }) {
  return (
    <div style={{
      position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
      background: ok ? "#1C1917" : "#A32D2D", color: "#FFF",
      padding: "10px 22px", borderRadius: 30, fontSize: 13, fontWeight: 500,
      boxShadow: "0 4px 20px rgba(0,0,0,0.18)", whiteSpace: "nowrap",
    }}>
      {ok ? "✓ " : "⚠ "}{msg}
    </div>
  );
}

// ── Main Portal ───────────────────────────────────────────────────────────────
/**
 * SubscriberPortal — customer self-service page
 *
 * Reads the pause token from URL: /portal/<token>
 * or accepts it as a prop: <SubscriberPortal token="abc123" />
 *
 * Router setup (React Router v6):
 *   <Route path="/portal/:token" element={<SubscriberPortalRoute />} />
 *
 *   function SubscriberPortalRoute() {
 *     const { token } = useParams();
 *     return <SubscriberPortal token={token} />;
 *   }
 *
 * Add this column to your mealbox_subscribers table in Supabase:
 *   ALTER TABLE mealbox_subscribers ADD COLUMN pause_token TEXT;
 *   CREATE UNIQUE INDEX ON mealbox_subscribers (pause_token);
 */
export default function SubscriberPortal({ token: tokenProp }) {
  const token = tokenProp || window.location.pathname.split("/portal/")[1]?.split("/")[0] || "";

  // Set a generic title immediately — will be updated once data loads
  useEffect(() => { document.title = "Meal Plan Portal"; }, []);

  const [subscriber,     setSubscriber]     = useState(null);
  const [provider,       setProvider]       = useState(null); // { name, logoUrl, color }
  const [loading,        setLoading]        = useState(true);
  const [notFound,       setNotFound]       = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [toast,          setToast]          = useState(null);

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    dbFetchSubscriberByToken(token)
      .then(async sub => {
        if (!sub) { setNotFound(true); setLoading(false); return; }
        setSubscriber(sub);
        // Use providerName stored on subscriber record (stamped on save)
        // Fall back to a separate brand fetch for older records that don't have it yet
        if (sub.providerName) {
          setProvider({ name: sub.providerName, logoUrl: "", color: "#1D9E75" });
        } else if (sub.userId) {
          dbFetchProviderBrand(sub.userId).then(brand => { if (brand) setProvider(brand); });
        }
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [token]);

  // ── Set page title once provider name is known ───────────────────────────
  useEffect(() => {
    if (provider?.name) {
      document.title = `${provider.name} - Customer Portal`;
    } else {
      document.title = "Customer Portal";
    }
  }, [provider]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3200);
  };

  const handlePauseSave = async (newPause) => {
    if (!subscriber || !newPause) return;

    // Safety net: reject a slot-overlapping pause even if it bypassed the modal.
    const conflict = findPauseConflict(subscriber.pausePeriods || [], newPause.from, newPause.to, newPause.slots, newPause.id);
    if (conflict) {
      showToast(`Pause overlaps an existing one (${conflict.from} → ${conflict.to}) on the same meal(s).`, false);
      return;
    }

    setSaving(true);
    const periods    = [...(subscriber.pausePeriods || []), newPause];
    const today      = new Date().toISOString().slice(0, 10);
    const paused     = periods.some(p => today >= p.from && today <= p.to);
    const newStatus  = paused ? "Paused" : (subscriber.status === "Paused" ? "Active" : subscriber.status);
    const newEndDate = calcEndDateWithPauses(subscriber, periods);

    try {
      const saved = await dbSavePortalPause(token, periods, newStatus, newEndDate);
      setSubscriber(saved);
      setShowPauseModal(false);
      showToast(`Pause saved! Your plan end date is now ${fmtDate(saved.endDate)}.`);
    } catch (err) {
      console.error("Portal pause save failed:", err.message);
      showToast(`Error: ${err.message}`, false);
    }
    setSaving(false);
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const s         = subscriber;
  const total     = s ? calcTotalMeals(s.totalDays, s.mealSlots) : 0;
  const delivered = s ? calcMealsDelivered(s.startDate, s.endDate, s.excludedDays, s.pausePeriods, s.mealSlots, total) : 0;
  const remaining = total - delivered;
  const pct       = total > 0 ? Math.round((delivered / total) * 100) : 0;
  const today     = new Date().toISOString().slice(0, 10);

  const allPauses      = s ? [...(s.pausePeriods || [])].sort((a, b) => b.from.localeCompare(a.from)) : [];
  const upcomingPauses = allPauses.filter(p => p.to   >= today).sort((a, b) => a.from.localeCompare(b.from));
  const pastPauses     = allPauses.filter(p => p.to   <  today);
  const mealBarColor   = remaining < 0 ? "#A32D2D" : remaining / (total || 1) < 0.2 ? "#D85A30" : "#1D9E75";
  const remainPct      = total > 0 ? Math.max(0, Math.min(100, Math.round((remaining / total) * 100))) : 0;

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", gap:12 }}>
          <div style={{ fontSize:36 }}>🍱</div>
          <div style={{ fontSize:14, color:"#78716C" }}>Loading your plan…</div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={pageStyle}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", gap:16, padding:"2rem", textAlign:"center" }}>
          <div style={{ fontSize:40 }}>🔗</div>
          <h2 style={{ fontSize:18, fontWeight:600, color:"#1C1917", margin:0 }}>Link not found</h2>
          <p style={{ fontSize:14, color:"#78716C", maxWidth:340, lineHeight:1.6, margin:0 }}>
            This link is invalid or may have expired. Please contact your meal provider for a new link.
          </p>
        </div>
      </div>
    );
  }

  const st = STATUS_STYLE[s.status] || STATUS_STYLE.Pending;
  const typeColor = TYPE_COLORS[s.type] || "#78716C";

  return (
    <div style={pageStyle}>
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}

      <div style={{ maxWidth:500, margin:"0 auto", padding:"2rem 1rem 5rem" }}>

        {/* Provider brand header */}
        <div style={{ textAlign:"center", marginBottom:"2rem" }}>
          {provider?.logoUrl ? (
            <img
              src={provider.logoUrl}
              alt={provider.name}
              style={{ maxHeight: 64, maxWidth: 200, objectFit: "contain", marginBottom: 8 }}
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: provider?.color || "#1C1917", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#FFF", margin: "0 auto 8px", letterSpacing: "-0.02em" }}>
                {(provider?.name || "M").slice(0, 1).toUpperCase()}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1C1917", letterSpacing: "-0.02em" }}>
                {provider?.name || "MealBox"}
              </div>
            </>
          )}
          <div style={{ fontSize: 12, color: "#A8A29E", marginTop: 4 }}>Your meal plan portal</div>
        </div>

        {/* Subscriber card */}
        <div style={cardStyle}>

          {/* Name + status */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1rem" }}>
            <div>
              <div style={{ fontSize:20, fontWeight:600, color:"#1C1917" }}>{s.name}</div>
              <div style={{ fontSize:13, color:typeColor, fontWeight:500, marginTop:3 }}>● {s.type} · {s.plan}</div>
            </div>
            <span style={{ fontSize:12, fontWeight:500, padding:"4px 12px", borderRadius:20, background:st.bg, color:st.color, flexShrink:0, marginLeft:8 }}>
              {s.status}
            </span>
          </div>

          {/* Meal slots */}
          <div style={{ display:"flex", gap:6, marginBottom:"1.25rem" }}>
            {(s.mealSlots || []).map(sl => (
              <span key={sl} style={{ fontSize:12, fontWeight:500, padding:"4px 12px", borderRadius:20, background:SLOT_COLORS[sl]+"18", color:SLOT_COLORS[sl] }}>
                {sl}
              </span>
            ))}
          </div>

          {/* Plan details grid */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:"1.25rem" }}>
            {[
              { label:"Plan starts",   value:fmtDate(s.startDate)  },
              { label:"Plan ends",     value:fmtDate(s.endDate)    },
              { label:"Delivery area", value:s.deliveryArea || "—" },
              { label:"Plan amount",   value:fmt(s.amount)         },
            ].map(item => (
              <div key={item.label} style={{ background:"#F7F6F2", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ fontSize:11, color:"#A8A29E", marginBottom:3 }}>{item.label}</div>
                <div style={{ fontSize:13, fontWeight:500, color:"#1C1917" }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* No-delivery days */}
          {(s.excludedDays || []).length > 0 && (
            <div style={{ background:"#F7F6F2", borderRadius:8, padding:"10px 12px", marginBottom:"1.25rem" }}>
              <div style={{ fontSize:11, color:"#A8A29E", marginBottom:8 }}>No delivery on</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {s.excludedDays.map(day => (
                  <span key={day} style={{ fontSize:12, fontWeight:500, padding:"4px 12px", borderRadius:20, background:"#1C1917", color:"#FFF" }}>
                    {day}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Meals remaining progress */}
          <div style={{ marginBottom:"1.25rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:500, color:"#1C1917" }}>Meals remaining</div>
              <div style={{ fontSize:13, fontWeight:600, color:mealBarColor }}>
                {remaining < 0 ? `${Math.abs(remaining)} overdue` : `${remaining} of ${total}`}
              </div>
            </div>
            <div style={{ height:8, background:"#F3F2EE", borderRadius:4, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${remainPct}%`, background:mealBarColor, borderRadius:4, transition:"width 0.5s ease" }} />
            </div>
            <div style={{ fontSize:11, color:"#A8A29E", marginTop:5 }}>
              {delivered} meals delivered · {pct}% of plan complete
            </div>
          </div>

          {/* Upcoming / active pauses */}
          {upcomingPauses.length > 0 && (
            <div style={{ background:"#E6F1FB", borderRadius:8, padding:"10px 14px", marginBottom:"1rem" }}>
              <div style={{ fontSize:12, fontWeight:500, color:"#0C447C", marginBottom:8 }}>⏸ Upcoming pauses</div>
              {upcomingPauses.map((p, i) => (
                <div key={p.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom: i < upcomingPauses.length - 1 ? 8 : 0, marginBottom: i < upcomingPauses.length - 1 ? 8 : 0, borderBottom: i < upcomingPauses.length - 1 ? "0.5px solid #C5DDEF" : "none" }}>
                  <div>
                    <div style={{ fontSize:12, color:"#185FA5", fontWeight:500 }}>
                      {fmtDate(p.from)} {p.from !== p.to ? `→ ${fmtDate(p.to)}` : ""}
                    </div>
                    <div style={{ display:"flex", gap:4, marginTop:3 }}>
                      {(p.slots||[]).map(sl => (
                        <span key={sl} style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:SLOT_COLORS[sl]+"22", color:SLOT_COLORS[sl], fontWeight:500 }}>
                          {sl}
                        </span>
                      ))}
                    </div>
                  </div>
                  {p.from > today && (
                    <span style={{ fontSize:9, background:"#0C447C", color:"#fff", padding:"2px 7px", borderRadius:8, flexShrink:0, marginLeft:8 }}>upcoming</span>
                  )}
                  {p.from <= today && p.to >= today && (
                    <span style={{ fontSize:9, background:"#185FA5", color:"#fff", padding:"2px 7px", borderRadius:8, flexShrink:0, marginLeft:8 }}>active now</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Past pause history */}
          {pastPauses.length > 0 && (
            <div style={{ background:"#F7F6F2", borderRadius:8, padding:"10px 14px", marginBottom:"1rem", border:"0.5px solid #E7E5E0" }}>
              <div style={{ fontSize:12, fontWeight:500, color:"#78716C", marginBottom:8 }}>🕐 Past pauses</div>
              {pastPauses.map((p, i) => (
                <div key={p.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom: i < pastPauses.length - 1 ? 8 : 0, marginBottom: i < pastPauses.length - 1 ? 8 : 0, borderBottom: i < pastPauses.length - 1 ? "0.5px solid #E7E5E0" : "none", opacity: 0.75 }}>
                  <div>
                    <div style={{ fontSize:12, color:"#44403C", fontWeight:500 }}>
                      {fmtDate(p.from)} {p.from !== p.to ? `→ ${fmtDate(p.to)}` : ""}
                    </div>
                    <div style={{ display:"flex", gap:4, marginTop:3 }}>
                      {(p.slots||[]).map(sl => (
                        <span key={sl} style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:SLOT_COLORS[sl]+"15", color:SLOT_COLORS[sl], fontWeight:500 }}>
                          {sl}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span style={{ fontSize:9, background:"#E7E5E0", color:"#78716C", padding:"2px 7px", borderRadius:8, flexShrink:0, marginLeft:8 }}>completed</span>
                </div>
              ))}
            </div>
          )}

          {/* Request pause button */}
          <button onClick={() => setShowPauseModal(true)}
            style={{ width:"100%", padding:"13px", fontSize:14, fontWeight:500, borderRadius:10, border:"1.5px solid #B5D4F4", background:"#E6F1FB", color:"#0C447C", cursor:"pointer", transition:"all 0.15s" }}>
            ⏸ Request a pause
          </button>
        </div>

        {/* Footer */}
        <div style={{ textAlign:"center", fontSize:12, color:"#A8A29E", marginTop:"1.5rem", lineHeight:1.7 }}>
          Need help? Contact your meal provider directly.<br />
          This portal is only for viewing your plan and requesting pauses.
        </div>
      </div>

      {/* PauseModal with isCustomerPortal=true — enforces tomorrow-onwards dates */}
      {showPauseModal && (
        <PauseModal
          subscriber={s}
          isCustomerPortal={true}
          onSave={(newPause) => handlePauseSave(newPause)}
          onClose={() => setShowPauseModal(false)}
        />
      )}
    </div>
  );
}

const pageStyle = {
  fontFamily: "'DM Sans','Segoe UI',sans-serif",
  background: "#F7F6F2",
  minHeight: "100vh",
  color: "#1a1a1a",
};

const cardStyle = {
  background: "#FFF",
  borderRadius: 16,
  border: "0.5px solid #E7E5E0",
  padding: "1.5rem",
  boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
};
